import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { InlineSpinner } from "@/components/common/Loading";
import { Building2, Plus, Search, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PlatformAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/platform/accounts", { params: { q } }); setAccounts(data.accounts); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "account_name", header: "Client Account", render: (r) => (
      <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" />
        <div><p className="font-medium text-slate-800">{r.account_name}</p>
          <p className="text-xs text-slate-400 font-mono">{r.internal_account_id}</p></div></div>) },
    { key: "orgs", header: "Organizations", render: (r) => r.org_count },
    { key: "users", header: "Users", render: (r) => r.user_count },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <PageHeader title="Super Admin · Client Accounts" description="Platform-level administration across all customers."
        actions={<Button className="bg-[#006090] hover:bg-[#004E75]" data-testid="create-account-button" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" /> New Client Account</Button>} />
      <div className="relative mb-4 max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Search accounts…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="account-search" />
      </div>
      <DataTable columns={columns} rows={accounts} loading={loading} testId="accounts-table"
        onRowClick={(r) => navigate(`/super-admin/accounts/${r.id}`)}
        empty={<EmptyState icon={ShieldCheck} title="No client accounts" description="Create the first client account." />} />
      <CreateAccountDialog open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); }} />
    </div>
  );
}

function CreateAccountDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ account_name: "", primary_contact_name: "", primary_contact_email: "", time_zone: "America/Los_Angeles" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.account_name) return setError("Account name is required");
    setError(""); setLoading(true);
    try {
      const payload = { ...form, primary_contact_email: form.primary_contact_email || null };
      await api.post("/platform/accounts", payload); toast.success("Client account created"); onCreated();
      setForm({ account_name: "", primary_contact_name: "", primary_contact_email: "", time_zone: "America/Los_Angeles" });
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="create-account-dialog">
        <DialogHeader><DialogTitle>New Client Account</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div><Label className="text-xs font-semibold text-slate-700">Account name</Label>
            <Input value={form.account_name} onChange={(e) => setForm({ ...form, account_name: e.target.value })} className="mt-1" data-testid="new-account-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-700">Primary contact</Label>
              <Input value={form.primary_contact_name} onChange={(e) => setForm({ ...form, primary_contact_name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold text-slate-700">Contact email</Label>
              <Input type="email" value={form.primary_contact_email} onChange={(e) => setForm({ ...form, primary_contact_email: e.target.value })} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs font-semibold text-slate-700">Time zone</Label>
            <Input value={form.time_zone} onChange={(e) => setForm({ ...form, time_zone: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="new-account-submit" className="bg-[#006090] hover:bg-[#004E75]">{loading ? <InlineSpinner /> : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
